import { FC, ReactNode, Suspense, useEffect, useRef } from 'react'
import i18n from 'i18next'
import { I18nextProvider } from 'react-i18next'
import type { Resource } from 'i18next'
import { captureException } from 'utils/sentry'
import i18nDefault from 'utils/i18n'

interface AnotherLanguageProps {
  children: ReactNode
  lng: string
  resources?: Resource
}

/*
Обертка над куском приложения, где нужно использовать другой язык.
Внутри использовать хук useTranslation

@example
<AnotherLanguage lng='ru'>
  <YourComponents />
</AnotherLanguage>
 */
const AnotherLanguage: FC<AnotherLanguageProps> = props => {
  const { children, lng, resources } = props

  const i18nInstance = useRef<typeof i18n | null>(null)
  if (i18nInstance.current === null) {
    if (resources) {
      i18nInstance.current = i18n.createInstance(
        {
          resources,
          lng: lng ?? i18nDefault.language,
          debug: process.env.NODE_ENV !== 'production',
        },
        err => err && captureException(err)
      )
    } else {
      i18nInstance.current = i18nDefault.cloneInstance()
    }
  }

  useEffect(() => {
    if (!i18nInstance.current) return
    i18nInstance.current.changeLanguage(lng || i18nDefault.language)
  }, [lng])

  return (
    <I18nextProvider i18n={i18nInstance.current}>
      <Suspense>{children}</Suspense>
    </I18nextProvider>
  )
}

export default AnotherLanguage
